import React from 'react';

import {
  MediaPresentational,
} from '../../components/ActivationTopSectionV2/ActivationTopSectionV2';
import {
  DeliveryMethodPresentational,
} from '../../components/ProductDetailsDeliveryMethod/ProductDetailsDeliveryMethod';
import {
  RedeeemPackageSubItemPresentational,
} from '../../components/RedeemBasketItem/RedeemBasketItem';
import {
  RedeeemPackageItemMainProductPresentational,
  RedeeemPackageItemPresentational,
} from '../../components/RedeemBasketModal/RedeemBasketModal';
import {
  BlankGiftCardPackProductVariationPresentational,
} from '../../components/ReorderProductsBlock/ReorderProductsBlock';
import { DeliveryMethod, Video } from '../../types/apiCompoundCommon';
import { RedeemPackageItem } from '../../types/apiCompoundRedeem';
import {
  GiftcardProductVariation,
  DonationProduct,
  GiftcardProduct,
  DonationRedeemData,
  GiftCardRedeemData,
  RedeemPackage,
  RedeemMainProduct,
  Media,
  EmbedVideo,
  SelfHostedVideo,
} from '../../types/apiTypes';
import { ProductListItemType, VideoPresentational } from '../../types/common';
import {
  GiftcardProductVariationPresentational,
  ProductListItemPresentational,
  RedeemDataPresentational,
} from '../../types/common';

export type MappedGiftCardProducts = {
  productsList: ProductListItemPresentational[] | null;
};

export const presentationalMappingHooks = {
  useMapOrderVariation(
    orderVariation?: GiftcardProductVariation | null,
  ): GiftcardProductVariationPresentational | null {
    return React.useMemo(() => {
      if (orderVariation) {
        return {
          id: orderVariation.product.id,
          title: orderVariation.product.title,
          price: orderVariation.price,
          variationType: orderVariation.variationType,
          sku: orderVariation.sku,
          productType: orderVariation.product.productType,
          product: {
            id: orderVariation.product.id,
            title: orderVariation.product.title,
          },
        };
      }
      return null;
    }, [orderVariation]);
  },

  useMapReorderVariation(
    reorderVariation?: BlankGiftCardPackProductVariationPresentational | null,
  ): GiftcardProductVariationPresentational | null {
    return React.useMemo(() => {
      if (reorderVariation) {
        return {
          id: reorderVariation.id,
          title: reorderVariation.title,
          price: reorderVariation.price,
          sku: reorderVariation.sku,
          productType: reorderVariation.product.productType,
          product: {
            id: reorderVariation.product.id,
            title: reorderVariation.product.title,
          },
        };
      }
      return null;
    }, [reorderVariation]);
  },

  useMappedGiftcardProducts(
    products?: Array<GiftcardProduct | DonationProduct>,
  ): MappedGiftCardProducts {
    const mapGiftCardProduct = presentationalMappingHooks.useMapGiftCardProduct();
    const mapDonationProduct = presentationalMappingHooks.useMapDonationProduct();

    const productsList = React.useMemo(() => {
      const list: ProductListItemPresentational[] = [];
      if (products === undefined) return null;

      if (products.length > 0) {
        products.forEach(product => {
          if (product.__typename === 'DonationProduct') {
            list.push(mapDonationProduct(product));
          }

          if (product.__typename === 'GiftcardProduct') {
            list.push(mapGiftCardProduct(product));
          }
        });
      }

      return list;
    }, [mapDonationProduct, mapGiftCardProduct, products]);

    return {
      productsList,
    };
  },

  useMapDonationProduct() {
    return React.useCallback((product: DonationProduct): ProductListItemPresentational => {
      return {
        id: product.id,
        type: ProductListItemType.DonationProduct,
        title: product.redeemData?.title || '',
        description: product.redeemData?.description || null,
        path: null,
        cardImage: product.image,
        backgroundImage: null,
        showHand: false,
        backgroundColor: product.backgroundColor,
        image: product.image,
        productType: product.productType,
      };
    }, []);
  },

  useMapGiftCardProduct() {
    return React.useCallback((product: GiftcardProduct):
      ProductListItemPresentational => {
      return {
        id: product.id,
        type: ProductListItemType.GiftcardProduct,
        title: product.title,
        description: product.description,
        path: product.path,
        cardImage: product.image,
        backgroundImage: null,
        showHand: product.showHand,
        backgroundColor: product.backgroundColor,
        image: product.image,
        productType: product.productType,
        showShadow: product.showShadow,
      };
    }, []);
  },

  useMapRedeemBasketItem() {
    return ((item: DonationRedeemData |
      GiftCardRedeemData | null): RedeemDataPresentational | null => {
      if (!item) return null;
      return {
        title: item.title,
        description: item.description,
        minimumAvailableCustomAmount: item.minimumAvailableCustomAmount,
        maximumAvailableCustomAmount: item.maximumAvailableCustomAmount,
      };
    });
  },

  useMapRedeemPackageSubItems() {
    return ((items: RedeemPackageItem[]): RedeeemPackageSubItemPresentational[] => {
      return items.map((item) => ({
        typename: item.__typename,
        ...(item.__typename === 'GiftcardOrderItem'
          && { variationType: item.variation.variationType }),
        variation: {
          id: item.variation.id,
          title: item.variation.title,
          price: item.variation.price,
        },
        qty: item.qty,
      }));
    });
  },

  useMapRedeemPackageMainProduct() {
    const mapRedeemData = presentationalMappingHooks.useMapRedeemBasketItem();

    return React.useCallback(
      (mainProduct: RedeemMainProduct): RedeeemPackageItemMainProductPresentational => ({
        productId: mainProduct.id,
        image: mainProduct.image,
        backgroundColor: mainProduct.backgroundColor,
        redeemData: mapRedeemData(mainProduct.redeemData),
      }), [mapRedeemData],
    );
  },

  useMapRedeemCartPackages(packages: RedeemPackage[] | null): RedeeemPackageItemPresentational[]
    | null {
    const mapItems = presentationalMappingHooks.useMapRedeemPackageSubItems();
    const mapMainProduct = presentationalMappingHooks.useMapRedeemPackageMainProduct();
    return React.useMemo(() => {
      if (!packages) return null;
      return packages.map((el) => ({
        id: el.id,
        packageTotals: el.packageTotals,
        items: mapItems(el.items),
        mainProduct: mapMainProduct(el.mainProduct),
        mainProductType: el.mainProductType,
      }));
    }, [packages, mapItems, mapMainProduct]);
  },

  useMapRedeemCartPackageCallback() {
    const mapItems = presentationalMappingHooks.useMapRedeemPackageSubItems();
    const mapMainProduct = presentationalMappingHooks.useMapRedeemPackageMainProduct();

    return React.useCallback((pack: RedeemPackage): RedeeemPackageItemPresentational => {
      return {
        id: pack.id,
        packageTotals: pack.packageTotals,
        items: mapItems(pack.items),
        mainProduct: mapMainProduct(pack.mainProduct),
        mainProductType: pack.mainProductType,
      };
    }, [mapItems, mapMainProduct]);
  },

  useMapRedeemB2BVideo(video: Video | null): VideoPresentational | null {
    return React.useMemo(() => {
      if (!video) return null;
      if (video.__typename === 'EmbedVideo') {
        return {
          provider: video.provider,
          url: video.url,
          typename: 'EmbedVideo',
        };
      }
      if (video.__typename === 'SelfHostedVideo') {
        return {
          mimeType: video.mimeType,
          url: video.url,
          typename: 'SelfHostedVideo',
        };
      }
      else return null;
    }, [video]);
  },

  useMapDeliveryMethod(methods: DeliveryMethod[] | null): DeliveryMethodPresentational[] | null {
    return React.useMemo(() => {
      return methods?.map(({ id }) => ({ id })) ?? null;
    }, [methods]);
  },
  useMapEntityMedia(heroBlockMedia?: Media) {
    return React.useMemo((): MediaPresentational | undefined => {
      if (!heroBlockMedia) return undefined;
      switch (heroBlockMedia?.__typename) {
        case 'Image': return {
          alt: heroBlockMedia.alt,
          url: heroBlockMedia.url,
          typename: 'Image',
        };
        case 'EmbedVideo': return {
          provider: heroBlockMedia.provider,
          typename: 'EmbedVideo',
          url: heroBlockMedia.url,
        };
        case 'SelfHostedVideo': return {
          mimeType: heroBlockMedia.mimeType,
          typename: 'SelfHostedVideo',
          url: heroBlockMedia.url,
        };
        default: return undefined;
      }
    }, [heroBlockMedia]);
  },
  useMapRedeemVideo(video: EmbedVideo | SelfHostedVideo | null) {
    return React.useMemo((): VideoPresentational | null => {
      switch (video?.__typename) {
        case 'EmbedVideo': return {
          provider: video.provider,
          typename: 'EmbedVideo',
          url: video.url,
        };
        case 'SelfHostedVideo': return {
          mimeType: video.mimeType,
          typename: 'SelfHostedVideo',
          url: video.url,
        };
        default: return null;
      }
    }, [video]);
  },
};
